'use client';

/**
 * Notification Panel Component
 * 
 * Dropdown panel that displays list of notifications with actions.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { X, CheckCheck, Trash2 } from 'lucide-react';
import { RootState, AppDispatch } from '@/app/store';
import {
  fetchNotifications,
  markAllAsRead,
  clearAllRead,
} from '@/app/store/thunks/notificationThunks';
import { NotificationItem } from './NotificationItem';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';

interface NotificationPanelProps {
  onClose: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation('common');
  const panelRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { notifications, isLoading, unreadCount } = useSelector(
    (state: RootState) => state.notification
  );

  // Fetch notifications on mount
  useEffect(() => {
    dispatch(fetchNotifications({ page: 1, pageSize: 20 }));
  }, [dispatch]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllAsRead());
  };

  const handleClearAll = async () => {
    await dispatch(clearAllRead());
  };

  if (!mounted) return null;

  const panel = (
    <div
      ref={panelRef}
      className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50"
      style={{
        top: '3rem',
        right: '0.5rem',
        width: 'min(384px, calc(100vw - 1rem))',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('notifications.title')}
          </h3>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('notifications.unreadCount', { count: unreadCount })}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Actions */}
      {notifications.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              {t('notifications.markAllRead')}
            </button>
          )}
          <button
            onClick={handleClearAll}
            className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            {t('notifications.clearAll')}
          </button>
        </div>
      )}

      {/* Notification List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Bell className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-gray-500 dark:text-gray-400">
              {t('notifications.empty.title')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 text-center">
          <button
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            onClick={() => {
              window.location.href = '/notifications';
              onClose();
            }}
          >
            {t('notifications.viewAll')}
          </button>
        </div>
      )}
    </div>
  );

  return createPortal(panel, document.body);
};

// Import Bell icon for empty state
import { Bell } from 'lucide-react';
